import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout, isTimeoutError, isNetworkError } from '../utils/networkError';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Plan tiers with pricing and features
const PLAN_TIERS = {
  free: {
    name: 'Free',
    price: 0,
    currency: 'GBP',
    appointments: 15,
    description: 'Perfect for getting started',
    features: [
      '15 appointments per month',
      'Basic booking widget',
      'Email confirmations',
      'Calendar sync',
      'Calendiary branding on widget',
    ],
    branding: true,
  },
  starter: {
    name: 'Starter',
    price: 27,
    currency: 'GBP',
    appointments: 100,
    description: 'For growing practices',
    features: [
      '100 appointments per month',
      'Customizable booking widget',
      'Email confirmations',
      'Calendar sync',
      'Remove Calendiary branding',
      'Priority support',
    ],
    branding: false,
  },
  professional: {
    name: 'Professional',
    price: 57,
    currency: 'GBP',
    appointments: 500,
    description: 'For busy practitioners',
    features: [
      '500 appointments per month',
      'Full widget customization',
      'Email confirmations',
      'Calendar sync',
      'Remove Calendiary branding',
      'Priority support',
      'Advanced analytics',
    ],
    branding: false,
    popular: true,
  },
  business: {
    name: 'Business',
    price: 97,
    currency: 'GBP',
    appointments: 1000,
    description: 'For clinics and teams',
    features: [
      '1000 appointments per month',
      'Full widget customization',
      'Email confirmations',
      'Calendar sync',
      'Remove Calendiary branding',
      'Priority support',
      'Advanced analytics',
      'Multi-doctor support',
      'Dedicated account manager',
    ],
    branding: false,
  },
};

const PLAN_ORDER = ['free', 'starter', 'professional', 'business'];

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const DoctorSubscription = () => {
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);

  // Upgrade state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);
  const [upgradeSuccess, setUpgradeSuccess] = useState(null);

  // Redirect if not authenticated or not a doctor
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch current subscription
  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'doctor' || !token) {
      return;
    }

    setIsLoadingSubscription(true);
    setSubscriptionError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/subscriptions/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!response.ok) {
        throw new Error('Failed to fetch subscription');
      }

      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      if (isTimeoutError(error)) {
        setSubscriptionError('Request timed out. Please try again.');
      } else if (isNetworkError(error)) {
        setSubscriptionError('Unable to connect to the server.');
      } else {
        setSubscriptionError(error.message);
      }
    } finally {
      setIsLoadingSubscription(false);
    }
  }, [isAuthenticated, user, token]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'doctor' && token) {
      fetchSubscription();
    }
  }, [isAuthenticated, user, token, fetchSubscription]);

  // Handle plan selection for upgrade
  const handleSelectPlan = (planKey) => {
    if (planKey === subscription?.planTier) {
      return; // Can't select current plan
    }

    // Check if this is a downgrade
    const currentIndex = PLAN_ORDER.indexOf(subscription?.planTier || 'free');
    const selectedIndex = PLAN_ORDER.indexOf(planKey);

    if (selectedIndex < currentIndex) {
      // Downgrade - not supported in this feature
      setUpgradeError('Downgrading is not available. Please contact support.');
      return;
    }

    setSelectedPlan(planKey);
    setShowPaymentModal(true);
    setUpgradeError(null);
  };

  // Process upgrade
  const handleUpgrade = async () => {
    if (!selectedPlan) return;

    setIsUpgrading(true);
    setUpgradeError(null);
    setUpgradeSuccess(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/subscriptions/upgrade`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ planTier: selectedPlan })
        },
        20000
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upgrade subscription');
      }

      const data = await response.json();

      // Update local subscription state
      setSubscription({
        ...subscription,
        planTier: selectedPlan,
        appointmentsLimit: PLAN_TIERS[selectedPlan].appointments
      });

      setUpgradeSuccess(`Successfully upgraded to ${PLAN_TIERS[selectedPlan].name} plan!`);
      setShowPaymentModal(false);
      setSelectedPlan(null);

      // Refresh subscription data
      fetchSubscription();
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      setUpgradeError(error.message);
    } finally {
      setIsUpgrading(false);
    }
  };

  // Cancel upgrade modal
  const handleCancelUpgrade = () => {
    setShowPaymentModal(false);
    setSelectedPlan(null);
    setUpgradeError(null);
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  const currentPlanKey = subscription?.planTier || 'free';
  const currentPlan = PLAN_TIERS[currentPlanKey];
  const currentPlanIndex = PLAN_ORDER.indexOf(currentPlanKey);

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/doctor/dashboard"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            Subscription Settings
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Manage your subscription plan and billing
          </p>
        </div>

        {/* Success Message */}
        {upgradeSuccess && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
            <p className="text-green-400 font-body">{upgradeSuccess}</p>
          </div>
        )}

        {/* Error Message */}
        {subscriptionError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400 font-body">{subscriptionError}</p>
            <button
              onClick={fetchSubscription}
              className="mt-2 font-mono text-xs uppercase tracking-widest text-red-400 hover:text-red-300 transition-smooth"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Current Plan Card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-8">
          <div className="flex justify-between items-start mb-4">
            <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
              Current Plan
            </h2>
            {isLoadingSubscription ? (
              <div className="animate-pulse h-6 w-24 bg-slate-200 dark:bg-silver/20 rounded"></div>
            ) : (
              <span className="px-3 py-1 bg-gradient-to-r from-silver/20 to-silver/10 rounded-full font-mono text-xs uppercase tracking-wider">
                {currentPlan?.name || 'Free'}
              </span>
            )}
          </div>

          {isLoadingSubscription ? (
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-slate-200 dark:bg-silver/20 rounded w-48"></div>
              <div className="h-4 bg-slate-100 dark:bg-silver/10 rounded w-full max-w-md"></div>
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-display text-5xl italic text-light-text dark:text-pure-white">
                  {currentPlan?.price === 0 ? 'Free' : `£${currentPlan?.price}`}
                </span>
                {currentPlan?.price > 0 && (
                  <span className="font-mono text-sm text-light-text-secondary dark:text-silver/60">/month</span>
                )}
              </div>

              {/* Usage Progress */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-body text-slate-600 dark:text-silver/80">Monthly Appointments</span>
                  <span className="font-mono text-lg">
                    <span className="text-light-text dark:text-pure-white">{subscription?.appointmentsUsed || 0}</span>
                    <span className="text-slate-400 dark:text-silver/40"> / {subscription?.appointmentsLimit || currentPlan?.appointments}</span>
                  </span>
                </div>
                <div className="relative h-2 bg-slate-100 dark:bg-charcoal rounded-full overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${
                      ((subscription?.appointmentsUsed || 0) / (subscription?.appointmentsLimit || currentPlan?.appointments)) * 100 >= 90
                        ? 'bg-gradient-to-r from-red-500 to-red-400'
                        : ((subscription?.appointmentsUsed || 0) / (subscription?.appointmentsLimit || currentPlan?.appointments)) * 100 >= 70
                        ? 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                        : 'bg-gradient-to-r from-silver to-pure-white'
                    }`}
                    style={{ width: `${Math.min(((subscription?.appointmentsUsed || 0) / (subscription?.appointmentsLimit || currentPlan?.appointments)) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {/* Current Plan Features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {currentPlan?.features?.slice(0, 4).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckIcon />
                    <span className="font-body text-sm text-slate-600 dark:text-silver/80">{feature}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Available Plans */}
        <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-6">
          {currentPlanIndex < PLAN_ORDER.length - 1 ? 'Upgrade Your Plan' : 'All Plans'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLAN_ORDER.map((planKey) => {
            const plan = PLAN_TIERS[planKey];
            const planIndex = PLAN_ORDER.indexOf(planKey);
            const isCurrent = planKey === currentPlanKey;
            const isUpgrade = planIndex > currentPlanIndex;
            const isDowngrade = planIndex < currentPlanIndex;

            return (
              <div
                key={planKey}
                className={`relative glass border rounded-2xl p-6 flex flex-col ${
                  plan.popular ? 'border-silver/30 ring-2 ring-silver/20' : 'border-light-border dark:border-border'
                } ${isCurrent ? 'opacity-75' : ''}`}
                data-plan={planKey}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-silver-gradient text-obsidian font-mono text-xs uppercase tracking-widest px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-green-500/20 text-green-400 font-mono text-xs uppercase tracking-widest px-3 py-1 rounded-full border border-green-500/30">
                      Current
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center pb-4 border-b border-light-border dark:border-border mb-4">
                  <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-display text-4xl italic text-light-text dark:text-pure-white">
                      {plan.price === 0 ? 'Free' : `£${plan.price}`}
                    </span>
                    {plan.price > 0 && (
                      <span className="font-mono text-sm text-light-text-secondary dark:text-silver/60">/mo</span>
                    )}
                  </div>
                  <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-2">{plan.description}</p>
                </div>

                {/* Appointments */}
                <div className="text-center mb-4">
                  <span className="font-display text-2xl italic text-light-text dark:text-pure-white">
                    {plan.appointments}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 ml-2">
                    appointments/mo
                  </span>
                </div>

                {/* Features */}
                <ul className="flex-1 space-y-2 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckIcon />
                      <span className="font-body text-xs text-slate-600 dark:text-silver/80">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Action Button */}
                <button
                  onClick={() => handleSelectPlan(planKey)}
                  disabled={isCurrent || isDowngrade}
                  className={`w-full py-3 px-6 rounded-lg font-mono text-xs uppercase tracking-widest transition-smooth ${
                    isCurrent
                      ? 'bg-slate-100 dark:bg-silver/10 text-slate-400 dark:text-silver/40 cursor-not-allowed'
                      : isDowngrade
                      ? 'bg-slate-100 dark:bg-silver/10 text-slate-400 dark:text-silver/40 cursor-not-allowed'
                      : isUpgrade
                      ? 'bg-silver-gradient text-obsidian hover:opacity-90'
                      : 'border border-light-border dark:border-border text-slate-600 dark:text-silver hover:bg-slate-100 dark:hover:bg-white/5'
                  }`}
                >
                  {isCurrent ? 'Current Plan' : isDowngrade ? 'Contact Support' : 'Upgrade'}
                </button>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-12 glass border border-light-border dark:border-border rounded-2xl p-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-6">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                What happens when I upgrade?
              </h3>
              <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                Your new plan takes effect immediately. You'll have access to the increased appointment limit right away.
              </p>
            </div>
            <div>
              <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                Can I downgrade my plan?
              </h3>
              <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                Downgrading requires contacting support to ensure a smooth transition. We'll prorate any differences.
              </p>
            </div>
            <div>
              <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                What if I exceed my limit?
              </h3>
              <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                We'll notify you when approaching your limit. You can upgrade anytime or wait until the next billing cycle.
              </p>
            </div>
            <div>
              <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                Is there a transaction fee?
              </h3>
              <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                Yes, all plans include a 1% transaction fee on paid appointments. This helps maintain the platform.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && (
        <div className="fixed inset-0 bg-obsidian/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6 w-full max-w-md">
            <h2 className="font-display text-2xl italic mb-4">
              Upgrade to {PLAN_TIERS[selectedPlan].name}
            </h2>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="font-body text-slate-600 dark:text-silver/80">New Plan</span>
                <span className="font-mono">{PLAN_TIERS[selectedPlan].name}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-body text-slate-600 dark:text-silver/80">Monthly Price</span>
                <span className="font-mono">£{PLAN_TIERS[selectedPlan].price}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-body text-slate-600 dark:text-silver/80">Appointment Limit</span>
                <span className="font-mono">{PLAN_TIERS[selectedPlan].appointments}/month</span>
              </div>
              <div className="border-t border-light-border dark:border-border mt-4 pt-4">
                <div className="flex justify-between items-center">
                  <span className="font-body text-light-text dark:text-pure-white">Due Today</span>
                  <span className="font-display text-2xl italic">£{PLAN_TIERS[selectedPlan].price}</span>
                </div>
              </div>
            </div>

            {/* Mock Payment Form */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                  Card Number
                </label>
                <input
                  type="text"
                  placeholder="4242 4242 4242 4242"
                  className="w-full px-4 py-3 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                  defaultValue="4242 4242 4242 4242"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                    Expiry
                  </label>
                  <input
                    type="text"
                    placeholder="MM/YY"
                    className="w-full px-4 py-3 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                    defaultValue="12/27"
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                    CVC
                  </label>
                  <input
                    type="text"
                    placeholder="123"
                    className="w-full px-4 py-3 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                    defaultValue="123"
                  />
                </div>
              </div>
            </div>

            {/* Error Message */}
            {upgradeError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 font-mono text-xs">{upgradeError}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={handleCancelUpgrade}
                disabled={isUpgrading}
                className="flex-1 py-3 px-6 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:bg-slate-100 dark:hover:bg-white/5 transition-smooth disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpgrade}
                disabled={isUpgrading}
                className="flex-1 py-3 px-6 bg-silver-gradient text-obsidian rounded-lg font-mono text-xs uppercase tracking-widest hover:opacity-90 transition-smooth disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUpgrading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  'Complete Payment'
                )}
              </button>
            </div>

            <p className="font-body text-xs text-slate-400 dark:text-silver/40 text-center mt-4">
              By completing this purchase, you agree to our terms of service.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorSubscription;
